export const CUSTOM_DOMAIN_STATUS_LIST = [
  'pending_dns',
  'verified',
  'active',
  'certificate_failed',
  'lost',
  'disabled',
  'removing',
] as const;

export type CustomDomainStatus = (typeof CUSTOM_DOMAIN_STATUS_LIST)[number];

export const CUSTOM_DOMAIN_STATUS_REASON_LIST = [
  'ownership_record_missing',
  'ownership_record_mismatch',
  'hostname_not_found',
  'points_elsewhere',
  'ipv6_record_present',
  'certificate_authority_forbidden',
  'dns_lookup_failed',
  'certificate_issuance_failed',
  'certificate_rate_limited',
  'edge_configuration_failed',
  'module_disabled',
  'workspace_inactive',
  'disabled_by_platform',
] as const;

export type CustomDomainStatusReason = (typeof CUSTOM_DOMAIN_STATUS_REASON_LIST)[number];

export type CustomDomainKind = 'subdomain' | 'root';

export type CustomDomainDnsRecordPurpose = 'routing' | 'ownership';

export type CustomDomainDnsRecordType = 'CNAME' | 'A' | 'TXT';

export interface CustomDomainDnsRecordApiResource {
  purpose: CustomDomainDnsRecordPurpose;
  record_type: CustomDomainDnsRecordType;
  name: string;
  value: string;
}

export interface CustomDomainApiResource {
  id: string;
  hostname: string;
  display_hostname: string;
  kind: CustomDomainKind;
  is_primary: boolean;
  status: CustomDomainStatus;
  status_reason: CustomDomainStatusReason | null;
  dns_records: CustomDomainDnsRecordApiResource[];
  is_disabled_by_platform: boolean;
  certificate_expires_at: string | null;
  last_checked_at: string | null;
  next_check_at: string | null;
  verified_at: string | null;
  activated_at: string | null;
  created_at: string | null;
}

export interface TenantCustomDomainApiResource extends CustomDomainApiResource {
  tenant_id: string;
}

export interface CustomDomainDnsRecord {
  purpose: CustomDomainDnsRecordPurpose;
  recordType: CustomDomainDnsRecordType;
  name: string;
  value: string;
}

export interface CustomDomain {
  id: string;
  hostname: string;
  displayHostname: string;
  kind: CustomDomainKind;
  isPrimary: boolean;
  status: CustomDomainStatus;
  statusReason: CustomDomainStatusReason | null;
  dnsRecords: CustomDomainDnsRecord[];
  isDisabledByPlatform: boolean;
  certificateExpiresAt: string | null;
  lastCheckedAt: string | null;
  nextCheckAt: string | null;
  verifiedAt: string | null;
  activatedAt: string | null;
  createdAt: string | null;
}

export interface TenantCustomDomain extends CustomDomain {
  tenantId: string;
}

function mapApiResourceToDnsRecord(
  resource: CustomDomainDnsRecordApiResource
): CustomDomainDnsRecord {
  return {
    purpose: resource.purpose,
    recordType: resource.record_type,
    name: resource.name,
    value: resource.value,
  };
}

export function mapApiResourceToCustomDomain(resource: CustomDomainApiResource): CustomDomain {
  return {
    id: resource.id,
    hostname: resource.hostname,
    displayHostname: resource.display_hostname,
    kind: resource.kind,
    isPrimary: resource.is_primary,
    status: resource.status,
    statusReason: resource.status_reason,
    dnsRecords: resource.dns_records.map(mapApiResourceToDnsRecord),
    isDisabledByPlatform: resource.is_disabled_by_platform,
    certificateExpiresAt: resource.certificate_expires_at,
    lastCheckedAt: resource.last_checked_at,
    nextCheckAt: resource.next_check_at,
    verifiedAt: resource.verified_at,
    activatedAt: resource.activated_at,
    createdAt: resource.created_at,
  };
}

export function mapApiResourceToTenantCustomDomain(
  resource: TenantCustomDomainApiResource
): TenantCustomDomain {
  return {
    ...mapApiResourceToCustomDomain(resource),
    tenantId: resource.tenant_id,
  };
}
